import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import { getReviewerAssignments } from '../../utils/api';

import { formatDateIST } from '../../utils/dateFormatter';
import { downloadPdfFile, extractFilename } from '../../utils/pdfHelper';

const MyAssignments = () => {
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAssignments();
    }, []);

    const fetchAssignments = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getReviewerAssignments();
            setAssignments(response.data || []);
        } catch (err) {
            console.error('Error fetching assignments:', err);
            setError(err.response?.data?.message || 'Failed to load assignments');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateIST(date);
  };

    const getSourceBadge = (source) => {
        return source === 'AUTO'
            ? <Badge variant="info">Auto-Assigned</Badge>
            : <Badge variant="primary">Manually Assigned</Badge>;
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <Loading fullScreen message="Loading your assignments..." />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 sm:mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Assignments</h1>
                        <p className="text-sm sm:text-base text-gray-600 mt-1">Papers assigned to you for review</p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <Button variant="secondary" onClick={() => navigate('/reviewer/bids')}>
                            My Bids
                        </Button>
                        <Button onClick={() => navigate('/reviewer/browse-conferences')}>
                            Browse Conferences
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{error}</p>
                        <button
                            onClick={fetchAssignments}
                            className="text-red-600 hover:text-red-800 font-medium mt-2"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {assignments.length === 0 ? (
                    <Card className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assignments Yet</h3>
                        <p className="text-gray-600 mb-6">
                            You haven't been assigned any papers to review yet. Place bids on papers you're interested in reviewing.
                        </p>
                        <Button onClick={() => navigate('/reviewer/browse-conferences')}>
                            Browse Available Papers
                        </Button>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <Card>
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-blue-600">{assignments.length}</div>
                                    <p className="text-gray-600 mt-2">Total Assigned</p>
                                </div>
                            </Card>
                            <Card>
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-green-600">
                                        {assignments.filter(a => a.source === 'AUTO').length}
                                    </div>
                                    <p className="text-gray-600 mt-2">Auto-Assigned</p>
                                </div>
                            </Card>
                            <Card>
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-purple-600">
                                        {assignments.filter(a => a.source === 'MANUAL').length}
                                    </div>
                                    <p className="text-gray-600 mt-2">Manually Assigned</p>
                                </div>
                            </Card>
                        </div>

                        {/* Assignments List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {assignments.map((assignment) => (
                                <Card key={assignment._id} hoverable>
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="font-bold text-gray-900 line-clamp-2 flex-1 pr-2">
                                            {assignment.submissionId?.title || 'Untitled Submission'}
                                        </h3>
                                        {getSourceBadge(assignment.source)}
                                    </div>

                                    <div className="space-y-2 mb-4">
                                        <p className="text-sm text-gray-600">
                                            <span className="font-medium">Conference:</span>{' '}
                                            {assignment.conferenceId?.name || 'Unknown Conference'}
                                        </p>
                                        {assignment.trackId?.name && (
                                            <p className="text-sm text-gray-600">
                                                <span className="font-medium">Track:</span>{' '}
                                                {assignment.trackId.name}
                                            </p>
                                        )}
                                        <p className="text-sm text-gray-600">
                                            <span className="font-medium">Match Score:</span>{' '}
                                            <span className="font-semibold">{assignment.matchScore}%</span>
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            <span className="font-medium">Assigned:</span>{' '}
                                            {formatDate(assignment.assignedAt)}
                                        </p>

                                            {assignment.conferenceId && (() => {
                                                const fallbackStart = assignment.conferenceId.startDate ? new Date(assignment.conferenceId.startDate) : new Date();
                                                const fallbackDeadline = new Date(fallbackStart);
                                                fallbackDeadline.setDate(fallbackDeadline.getDate() - 7);
                                                const deadline = assignment.conferenceId.reviewDeadline ? new Date(assignment.conferenceId.reviewDeadline) : fallbackDeadline;
                                            const now = new Date();
                                            const daysRemaining = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
                                            const isOverdue = daysRemaining < 0;
                                            const isUrgent = daysRemaining <= 2 && !isOverdue;
                                            const isWarning = daysRemaining > 2 && daysRemaining <= 5;

                                            return (
                                                <div className={`mt-3 p-3 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' :
                                                        isUrgent ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
                                                            isWarning ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200' :
                                                                'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                                                    }`}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`text-xl ${isOverdue ? 'text-red-500' :
                                                                isUrgent ? 'text-red-500' :
                                                                    isWarning ? 'text-yellow-500' :
                                                                        'text-green-500'
                                                            }`}>
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-xs font-medium text-gray-600">Review Deadline</p>
                                                            <p className={`text-sm font-bold ${isOverdue ? 'text-red-600' :
                                                                    isUrgent ? 'text-red-600' :
                                                                        isWarning ? 'text-yellow-700' :
                                                                            'text-green-700'
                                                                }`}>
                                                                {formatDateIST(deadline)}
                                                            </p>
                                                        </div>
                                                        <div className={`px-2 py-1 rounded-full text-xs font-bold ${isOverdue ? 'bg-red-100 text-red-700' :
                                                                isUrgent ? 'bg-red-100 text-red-700' :
                                                                    isWarning ? 'bg-yellow-100 text-yellow-700' :
                                                                        'bg-green-100 text-green-700'
                                                            }`}>
                                                            {isOverdue
                                                                ? `${Math.abs(daysRemaining)}d overdue`
                                                                : daysRemaining === 0
                                                                    ? 'Due today!'
                                                                    : `${daysRemaining}d left`
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {(() => {
                                        const rubricUrl = assignment.trackId?.rubricUrl || assignment.conferenceId?.defaultRubricUrl;
                                        const rubricName = assignment.trackId?.rubricOriginalName || assignment.conferenceId?.defaultRubricOriginalName || 'Rubric';

                                        return (
                                            <div className="flex gap-2">
                                                {rubricUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => downloadPdfFile(rubricUrl, extractFilename(rubricUrl, rubricName))}
                                                        title="Download Evaluation Rubric"
                                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors flex-shrink-0"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        Rubric
                                                    </button>
                                                )}
                                                <Button
                                                    fullWidth
                                                    onClick={() => navigate(`/reviewer/review/${assignment.submissionId?._id}`)}
                                                >
                                                    Write Review
                                                </Button>
                                            </div>
                                        );
                                    })()}
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MyAssignments;
